// src/contexts/auth/hooks.ts
/** @jsxImportSource @emotion/react */

import React, { useCallback, useMemo } from "react";

import type { Profile, RegistrationData, Session, User } from "./types";

import {
    api,
    clearSession as clearApiSession,
    setSession as setApiSession,
} from "../../api/api";
import { isNative } from "../../platform";
import { storageSet } from "../../platform/storage";
import type { AuthResponse } from "../../api/auth/auth.types";

import { useAuthForm } from "./hooks/form";
import { useAuthSession } from "./hooks/session";
import { useAuthBoot } from "./hooks/boot";
import { useAuthDev } from "./hooks/auth-dev";
import { makeAuthLogger } from "./hooks/storage";
import { useAuthOnboarding } from "./hooks/registration";

import { authApi, profileApi, readStoredSession } from "../../api";
import { bootApi } from "../../api/boot/boot";
import { ENVIRONMENT } from "../../env";

import { readStoredTokens, readStoredUserId } from "./helpers";
import { unsubscribePush } from "../../utils/push-utils";
import { trackEvent } from "../../utils/analytics";
import { BiometricsStorage } from "../../helpers/biometrics-storage";

export const useAuthHooks = (deps: {
    // state values
    email: string;
    passwordRef: React.MutableRefObject<string>;
    session: Session | null;
    user: User | null;
    profile: Profile | null;
    onboarding: RegistrationData;
    currentStepState: number;
    userId: string | undefined;

    // setters
    setEmailState: (v: string) => void;
    setPasswordState: (v: string) => void;

    setSession: (v: Session | null) => void;
    setUser: (v: User | null) => void;
    setProfile: (v: Profile | null) => void;

    setRegistrationState: React.Dispatch<
        React.SetStateAction<RegistrationData>
    >;
    setCurrentStepState: (v: number) => void;
    setUserId: (v: string | undefined) => void;

    setProfileLoaded: (v: boolean) => void;
    setProfileSaving: (v: boolean) => void;

    // ✅ NEW
    setProfileRefreshing: (v: boolean) => void;

    setSessionLoading: (v: boolean) => void;
    setAuthLoading: (v: boolean) => void;
    setError: (v: string | null) => void;
}) => {
    const {
        email,
        passwordRef,
        session,
        user,
        profile,
        onboarding,
        currentStepState,
        userId,

        setEmailState,
        setPasswordState,

        setSession,
        setUser,
        setProfile,

        setRegistrationState,
        setCurrentStepState,
        setUserId,

        setProfileLoaded,
        setProfileSaving,
        setProfileRefreshing, // ✅ NEW

        setSessionLoading,
        setAuthLoading,
        setError,
    } = deps;

    // ✅ stabilize logger identity
    const L = useMemo(() => makeAuthLogger("hooks"), []);

    L.log("[useAuthHooks] render", {
        hasSession: !!session,
        hasUser: !!user,
        hasProfile: !!profile,
        userId,
        onboardingKeys: Object.keys(onboarding ?? {}),
        currentStepState,
    });

    // ---------- env-aware base URL builder ----------
    const ENV = String(import.meta.env.VITE_ENVIRONMENT ?? "").toUpperCase();
    // const isDevEnv = ENV === "DEV" || import.meta.env.DEV;
    const isDevEnv = ENV === "DEV";

    const DEV_BASE =
        (import.meta.env.VITE_DEV_BASE_URL as string | undefined) ??
            window.location.origin;
    const PROD_BASE =
        (import.meta.env.VITE_APP_BASE_URL as string | undefined) ??
            window.location.origin;

    const buildBaseUrl = useCallback(
        () => (isDevEnv ? DEV_BASE : PROD_BASE),
        [isDevEnv, DEV_BASE, PROD_BASE],
    );

    const devToolsEnabled = ENVIRONMENT === "DEV";

    /* ------------------------------ Split hooks ------------------------------ */

    const form = useAuthForm({
        email,
        passwordRef,
        setEmailState,
        setPasswordState,
    });

    const sessionCore = useAuthSession({
        setSession,
        setUser,
    });

    const onboardingCore = useAuthOnboarding({
        onboarding,
        currentStepState,
        userId,
        setRegistrationState,
        setCurrentStepState,
        setUserId,
        setProfileLoaded,
        setProfileSaving,
        setProfileRefreshing, // ✅ NEW
        setProfile,
        setUser,
    });

    /* ------------------------------ ✅ rehydrate auth from storage ------------------------------ */

    const syncAuthFromStorage = useCallback(async () => {
        L.log("[syncAuthFromStorage] start");

        const stored = readStoredSession();

        L.log("[syncAuthFromStorage] stored snapshot", {
            hasAccess: !!stored.access_token,
            hasRefresh: !!stored.refresh_token,
            expires_at: stored.expires_at,
            user_id: stored.user_id,
        });

        if (!stored.access_token || !stored.refresh_token) {
            L.log("[syncAuthFromStorage] no tokens -> clear in-memory");

            setSession(null);
            setUser(null);
            setProfile(null);
            setUserId(undefined);

            clearApiSession({ emit: false });

            await onboardingCore.syncSession();
            L.log("[syncAuthFromStorage] done (no tokens)");
            return;
        }

        // Ensure axios header matches storage tokens (NO emit to avoid loops)
        setApiSession(stored.access_token, stored.refresh_token, {
            emit: false,
        });

        // Use combined boot endpoint to load profile + payment + conversation
        let meProfile: Profile | null = null;
        try {
            const bootData = await bootApi.getMe();
            meProfile = bootData.profile;
            setProfile(meProfile);
            setUser((meProfile as any) ?? null);
            setSession({
                access_token: stored.access_token,
                refresh_token: stored.refresh_token,
                expires_in: 0,
                user: ((meProfile as any) ?? null) as any,
            });
            await onboardingCore.syncSession({ profile: bootData.profile });
            if (bootData.payment) {
                window.dispatchEvent(new CustomEvent("boot:payment", { detail: bootData.payment }));
            }
            if (bootData.canonical_conversation) {
                window.dispatchEvent(new CustomEvent("boot:conversation", { detail: bootData.canonical_conversation }));
            }
        } catch {
            L.warn("[syncAuthFromStorage] boot endpoint failed, falling back");
            try {
                meProfile = await profileApi.getMe();
            } catch {
                meProfile = null;
            }
            setProfile(meProfile);
            setUser((meProfile as any) ?? null);
            setSession({
                access_token: stored.access_token,
                refresh_token: stored.refresh_token,
                expires_in: 0,
                user: ((meProfile as any) ?? null) as any,
            });
            await onboardingCore.syncSession();
        }

        L.log("[syncAuthFromStorage] done");
    }, [L, onboardingCore, setProfile, setSession, setUser, setUserId]);

    /* ------------------------------ Auth actions ------------------------------ */

    const signUp = useCallback(
        async (emailArg: string, _password: string) => {
            L.log("[signUp] start", { email: emailArg });

            setAuthLoading(true);
            setError(null);
            try {
                L.log("[signUp] authApi.signupEmail()");
                await authApi.signupEmail(emailArg);
                L.log("[signUp] success");
                trackEvent('signup_email_sent', { method: 'email' });
            } catch (e: any) {
                const msg = String(
                    e?.response?.data?.detail ||
                        e?.response?.data?.error ||
                        e?.message ||
                        "Signup failed.",
                );
                L.log("[signUp] error msg:", msg);
                trackEvent('signup_failed', { method: 'email', error: msg });
                setError(msg);
                throw e;
            } finally {
                setAuthLoading(false);
                L.log("[signUp] done -> setAuthLoading(false)");
            }
        },
        [L, setAuthLoading, setError],
    );

    const signIn = useCallback(
        async (emailArg: string, passwordArg: string): Promise<Session> => {
            L.log("[signIn] start", {
                email: emailArg,
                hasPassword: !!passwordArg,
            });

            setAuthLoading(true);
            setError(null);

            try {
                L.log("[signIn] authApi.signinPassword()");
                const data: AuthResponse = await authApi.signinPassword(
                    emailArg,
                    passwordArg,
                );

                L.log("[signIn] tokens received", {
                    hasAccess: !!data?.access_token,
                    hasRefresh: !!data?.refresh_token,
                    expires_in: data?.expires_in,
                    hasUser: !!data?.user,
                });

                await sessionCore.setAuthStateFromTokens(data);
                trackEvent('login_success', { method: 'password' });

                // Use combined boot endpoint to load profile + payment + conversation
                try {
                    const bootData = await bootApi.getMe();
                    await onboardingCore.syncSession({ profile: bootData.profile });
                    if (bootData.payment) {
                        window.dispatchEvent(new CustomEvent("boot:payment", { detail: bootData.payment }));
                    }
                    if (bootData.canonical_conversation) {
                        window.dispatchEvent(new CustomEvent("boot:conversation", { detail: bootData.canonical_conversation }));
                    }
                } catch {
                    L.warn("[signIn] boot endpoint failed, falling back to syncSession");
                    await onboardingCore.syncSession();
                }

                setSessionLoading(true);
                setTimeout(() => setSessionLoading(false), 0);

                const nextSession: Session = {
                    access_token: data.access_token,
                    refresh_token: data.refresh_token,
                    expires_in: data.expires_in,
                    user: (data.user as User) ?? null,
                };

                L.log("[signIn] done -> returning nextSession", {
                    hasUser: !!nextSession.user,
                    expires_in: nextSession.expires_in,
                });

                return nextSession;
            } catch (e: any) {
                const msg = String(
                    e?.response?.data?.detail ||
                        e?.response?.data?.error ||
                        e?.message ||
                        "Login failed.",
                );
                L.log("[signIn] error msg:", msg);
                trackEvent('login_failed', { method: 'password', error: msg });
                setError(msg);
                throw e;
            } finally {
                setAuthLoading(false);
                L.log("[signIn] finally -> setAuthLoading(false)");
            }
        },
        [
            L,
            onboardingCore,
            sessionCore,
            setAuthLoading,
            setError,
            setSessionLoading,
        ],
    );

    const signinWithPassword = useCallback(async (): Promise<Session> => {
        L.log("[signinWithPassword] start", { email });

        const e = email.trim();
        const pwd = passwordRef.current;

        if (!e) throw new Error("Email is required.");
        if (!pwd) throw new Error("Password is required.");

        const s = await signIn(e, pwd);

        const stored = readStoredTokens();
        const storedUserId = readStoredUserId();

        const best: Session | null = session ??
            (stored.access_token && stored.refresh_token
                ? ({
                    access_token: stored.access_token,
                    refresh_token: stored.refresh_token,
                    expires_in: s.expires_in ?? 0,
                    user: user ?? s.user ?? null,
                } as Session)
                : null);

        L.log("[signinWithPassword] best session computed", {
            fromState: !!session,
            fromStored: !!(stored.access_token && stored.refresh_token),
            hasBest: !!best,
            hasBestUser: !!best?.user,
            storedUserIdPresent: !!storedUserId,
        });

        if (!best) throw new Error("No session established after login.");
        return best;
    }, [L, email, passwordRef, signIn, session, user]);

    const signOut = useCallback(async () => {
        L.log("[signOut] start");

        // Flag so the login screen knows this was a manual logout
        // (vs session expiry) and skips auto-biometric prompt.
        try { sessionStorage.setItem('manual_logout', 'true'); } catch { /* ignore */ }

        setAuthLoading(true);
        setError(null);
        try {
            const biometricsEnabled = isNative() && BiometricsStorage.isBiometricsEnabled();

            // Biometric users: snapshot current refresh token into Keychain
            // before clearing local state so it survives for biometric re-login.
            if (biometricsEnabled) {
                const rt = localStorage.getItem('refresh_token');
                const uid = localStorage.getItem('user_id');
                if (rt && uid) {
                    const { storeNativeCredentials } = await import("../../helpers/native-biometric");
                    await storeNativeCredentials(uid, rt).catch(() => {});
                }
            }

            if (biometricsEnabled) {
                // Soft logout: clear local tokens only, keep server session alive
                // so the Keychain refresh token remains valid for biometric re-login.
                L.log("[signOut] biometrics enabled — soft logout (local-only)");
                clearApiSession();
            } else {
                L.log("[signOut] authApi.signOut()");
                await authApi.signOut();
            }
            trackEvent('signed_out');

            // Unsubscribe from push notifications (fire-and-forget)
            void unsubscribePush();

            // Clear native biometric credentials only when biometrics is disabled
            if (isNative() && !biometricsEnabled) {
                import("../../helpers/native-biometric").then(
                    (m) => m.clearNativeCredentials(),
                ).catch(() => {});
            }

            localStorage.removeItem("theme_id");
            localStorage.removeItem("conversation_id");

            // Mark this device as having a registered account so returning
            // users see the login screen instead of the invitation code screen.
            storageSet("mailander_has_account", "true");

            setSession(null);
            setUser(null);
            setProfile(null);
            setUserId(undefined);
            setRegistrationState({});
            setCurrentStepState(0);
            setProfileLoaded(true);
            setProfileRefreshing(false); // ✅ ensure refresh flag resets on logout

            // keep: sign out is an intentional global state transition
            sessionCore.emitAuthChange();
        } finally {
            setAuthLoading(false);
            L.log("[signOut] done -> setAuthLoading(false)");
        }
    }, [
        L,
        sessionCore,
        setAuthLoading,
        setCurrentStepState,
        setError,
        setRegistrationState,
        setProfileLoaded,
        setProfileRefreshing,
        setProfile,
        setSession,
        setUser,
        setUserId,
    ]);

    const oauthGoogle = useCallback(async () => {
        L.log("[oauthGoogle] start");

        setAuthLoading(true);
        setError(null);
        try {
            // On native, redirect back to the custom URL scheme so the deep link handler picks it up
            const redirect_to = isNative()
                ? 'mailander://auth/callback'
                : `${buildBaseUrl()}/login`;

            L.log("[oauthGoogle] GET /auth/oauth-url", { redirect_to });

            const res = await api.get("/auth/oauth-url", {
                params: { provider: "google", redirect_to },
            });

            const data = res.data as { url: string };
            L.log("[oauthGoogle] response", { hasUrl: !!data?.url });

            if (!data?.url) throw new Error("No OAuth URL returned");

            trackEvent('oauth_started', { provider: 'google' });

            if (isNative()) {
                // Open OAuth in in-app browser — redirect will be caught by deep link handler
                const { Browser } = await import('@capacitor/browser');
                await Browser.open({ url: data.url });
            } else {
                window.location.href = data.url;
            }
        } catch (e: any) {
            const msg = String(
                e?.response?.data?.detail ||
                    e?.response?.data?.error ||
                    e?.message ||
                    "OAuth init failed.",
            );
            L.log("[oauthGoogle] error msg:", msg);
            setError(msg);
            throw e;
        } finally {
            setAuthLoading(false);
            L.log("[oauthGoogle] done -> setAuthLoading(false)");
        }
    }, [L, buildBaseUrl, setAuthLoading, setError]);

    const signInWithGoogle = oauthGoogle;
    const signUpWithGoogle = oauthGoogle;

    const biometricLogin = useCallback(
        async (access_token: string, refresh_token: string) => {
            L.log("[biometricLogin] start", {
                hasAccess: !!access_token,
                hasRefresh: !!refresh_token,
            });

            setAuthLoading(true);
            setError(null);
            try {
                setApiSession(access_token, refresh_token, { emit: false });

                const tokensForState: AuthResponse = {
                    access_token,
                    refresh_token,
                    expires_in: 0,
                    user: (user as any) ?? (null as any),
                };

                await sessionCore.setAuthStateFromTokens(tokensForState);

                // Use combined boot endpoint
                try {
                    const bootData = await bootApi.getMe();
                    await onboardingCore.syncSession({ profile: bootData.profile });
                    if (bootData.payment) {
                        window.dispatchEvent(new CustomEvent("boot:payment", { detail: bootData.payment }));
                    }
                    if (bootData.canonical_conversation) {
                        window.dispatchEvent(new CustomEvent("boot:conversation", { detail: bootData.canonical_conversation }));
                    }
                } catch {
                    L.warn("[biometricLogin] boot endpoint failed, falling back to syncSession");
                    await onboardingCore.syncSession();
                }

                setSessionLoading(true);
                setTimeout(() => setSessionLoading(false), 0);

                trackEvent('login_success', { method: 'biometric' });
                L.log("[biometricLogin] done");
            } catch (e: any) {
                const msg = String(
                    e?.response?.data?.detail ||
                        e?.response?.data?.error ||
                        e?.message ||
                        "Biometric login failed.",
                );
                L.log("[biometricLogin] error msg:", msg);
                trackEvent('login_failed', { method: 'biometric', error: msg });
                setError(msg);
                throw e;
            } finally {
                setAuthLoading(false);
                L.log("[biometricLogin] finally -> setAuthLoading(false)");
            }
        },
        [
            L,
            onboardingCore,
            sessionCore,
            setAuthLoading,
            setError,
            setSessionLoading,
            user,
        ],
    );

    const bootCore = useAuthBoot({
        setError,
        setSessionLoading,
        setSession,
        setUser,
        emitAuthChange: sessionCore.emitAuthChange,
        setAuthStateFromTokens: sessionCore.setAuthStateFromTokens,
        tryRefreshIfNeeded: sessionCore.tryRefreshIfNeeded,
        syncSession: onboardingCore.syncSession,
    });

    const dev = useAuthDev({
        session,
        user,
        userId,
        onboarding,
        currentStepState,
        setSession,
        setUser,
        setUserId,
        setRegistrationState,
        setCurrentStepState,
        setError,
        signOut,
        emitAuthChange: sessionCore.emitAuthChange,
    });

    return useMemo(
        () => ({
            isDevEnv,
            devToolsEnabled,
            buildBaseUrl,

            ...form,

            smartMerge: onboardingCore.smartMerge,
            setRegistration: onboardingCore.setRegistration,
            setCurrentStep: onboardingCore.setCurrentStep,

            emitAuthChange: sessionCore.emitAuthChange,
            setAuthStateFromTokens: sessionCore.setAuthStateFromTokens,
            tryRefreshIfNeeded: sessionCore.tryRefreshIfNeeded,

            syncSession: onboardingCore.syncSession,
            syncAuthFromStorage,

            signUp,
            signIn,
            signinWithPassword,
            signOut,
            signInWithGoogle,
            signUpWithGoogle,
            biometricLogin,

            persistRegistrationIfNeeded:
                onboardingCore.persistRegistrationIfNeeded,

            devTokens: dev.devTokens,
            clearState: dev.clearState,
            hardLogout: dev.hardLogout,
            dump: () => ({
                location: {
                    href: window.location.href,
                    origin: window.location.origin,
                    pathname: window.location.pathname,
                    search: window.location.search,
                    hash: window.location.hash,
                },
                session,
                user,
                userId,
                profile,
                onboarding,
                currentStep: currentStepState,
                tokenStorage: {
                    ...readStoredTokens(),
                    user_id: readStoredUserId(),
                },
            }),

            boot: bootCore.boot,
        }),
        [
            isDevEnv,
            devToolsEnabled,
            buildBaseUrl,
            form,
            onboardingCore.smartMerge,
            onboardingCore.setRegistration,
            onboardingCore.setCurrentStep,
            sessionCore.emitAuthChange,
            sessionCore.setAuthStateFromTokens,
            sessionCore.tryRefreshIfNeeded,
            onboardingCore.syncSession,
            syncAuthFromStorage,
            signUp,
            signIn,
            signinWithPassword,
            signOut,
            signInWithGoogle,
            signUpWithGoogle,
            biometricLogin,
            onboardingCore.persistRegistrationIfNeeded,
            dev.devTokens,
            dev.clearState,
            dev.hardLogout,
            bootCore.boot,
            session,
            user,
            userId,
            profile,
            onboarding,
            currentStepState,
        ],
    );
};
